// Supply-run session: the setup + headless resolution of a single POI search, shared
// by the meta-loop harness (which runs many supply runs across days) and — once the
// V4 app rewrite lands — the real app's supply-run mode. Extracted verbatim in
// behavior from meta-loop-debug.html's runOnePoiSupplyRun/survivorToUnit/
// buildEnemiesForZone so the harness stays a faithful preview of what the app
// runs (see plans/V4_APP_REWRITE_PLAN.md, phase 2).
//
// Geometry dependencies (the road graph + building set) are injected, not
// imported as singletons, so this stays testable against the committed
// fixture and reusable against the app's live map data. Threat is intentionally
// NOT a dependency here: the caller derives the enemy count (e.g. via
// threat.mjs's threatToEnemyCount) and passes it in, keeping this module focused
// on "run a supply run given N enemies."

import { createTacticalState, DEFAULT_CARRY_CAPACITY } from "./tactical-turn.mjs";
import { professionCategory } from "./professions-v4.mjs";
import { runTacticalEncounterToCompletion } from "./auto-tactical-run.mjs";
import { buildTacticalZone } from "./tactical-zone.mjs";
import { buildNavGrid, buildingDoorsteps, findTerrainPath } from "./tactical-pathfinding.mjs";
import { pointInBuildings } from "./line-of-sight.mjs";
import { ENEMY_STATES, ENEMY_TYPES, GUARD_ROTATE_MAX_TURNS, GUARD_ROTATE_MIN_TURNS } from "./tactical-enemy-ai.mjs";
import { facingBearingDegrees } from "./vision-cone.mjs";
import { resolveTacticalZoneOutcome } from "./permadeath.mjs";
import { getItem, inventoryValue } from "./items.mjs";
import { equipmentLoadoutMap, equipmentLoadoutSlot, getEquipmentEffects } from "./survivor-upgrades.mjs";
import { SURVIVOR_MAX_HEALTH } from "./combat-math.mjs";
import { distanceInMeters, findRouteBetweenPoints } from "../road-graph.mjs";

export const DEFAULT_TACTICAL_ZONE_RADIUS_METERS = 150;
export const DEFAULT_OFF_STREET_MULTIPLIER = 2;
export const DEFAULT_TACTICAL_MOVE_METERS_PER_TURN = 60; // infantry pace inside a supply-run zone
export const DEFAULT_TACTICAL_VISION_CONE_DEGREES = 90;
export const DEFAULT_TACTICAL_SIGHT_METERS = 70;
export const ENEMY_SPEED_METERS_PER_GAME_MINUTE = 18;
export const ENEMY_SPAWN_MIN_SPACING_METERS = 60;
// Keep enemies from spawning right on top of the squad's entry: a corridor
// whose seed point is within this radius of the squad spawn is only used as a
// last resort. A little past sight range (70m) so the squad isn't already
// spotted on turn 1.
export const ENEMY_SPAWN_AVOID_SPAWN_METERS = 90;
// How far inside the boundary crossing the squad forms up, and the tangential
// gap between squad members. The entry crossing sits exactly on the zone
// circle, so units spawned there (and spread) can land just outside it — inset
// them along the inward radial and spread them along the boundary tangent so
// the whole squad reads as just inside the entrance.
export const SQUAD_SPAWN_INSET_METERS = 20;
// The squad always forms up together at the single entrance nearest where the
// base->target route came in (now that survivors must physically discover buildings,
// splitting them across separate streets was disorienting). This is the gap
// between adjacent survivors in that tight starting formation.
export const SQUAD_FORMATION_GAP_METERS = 5;
// The extraction band: a ring just outside the supply-run circle whose streets are
// walkable (the green "exit lanes"). The nav grid extends this far so units can
// step onto it; tactical-view lights its streets and fogs everything past it.
export const EXTRACTION_BAND_METERS = 16;

// Build a tactical unit from a roster survivor, folding in equipped item
// effects. Bag capacity starts at four and equipment can increase it.
export function createTacticalUnit(survivor, {
  baseMoveMetersPerTurn = DEFAULT_TACTICAL_MOVE_METERS_PER_TURN,
  baseCarryCapacity = DEFAULT_CARRY_CAPACITY,
} = {}) {
  const effects = getEquipmentEffects(survivor?.equipmentItemIds);
  return {
    id: survivor.id,
    survivorId: survivor.id,
    label: survivor.name ?? survivor.label ?? survivor.id,
    // Combat HP carried into the run from the campaign survivor: a survivor who
    // came home wounded from a prior supply run enters this one still hurt (they heal
    // 1 HP/day back at base). Backfills to full for survivors with no health yet.
    health: {
      current: survivor.health?.current ?? SURVIVOR_MAX_HEALTH,
      max: survivor.health?.max ?? SURVIVOR_MAX_HEALTH,
    },
    // The equipped loadout carried into the run; it can change mid-run as the
    // unit equips gear from its bag (see equipCarriedItem). Base move/carry are
    // stashed so the derived stats can be recomputed on each swap.
    equipmentItemIds: [...(survivor?.equipmentItemIds ?? [])],
    baseMoveMetersPerTurn,
    baseCarryCapacity,
    moveMetersPerTurn: baseMoveMetersPerTurn + effects.moveBonusMetersPerTurn,
    carryCapacity: baseCarryCapacity + effects.carryCapacityBonus,
    offenseBonus: effects.offenseBonus,
    defenseBonus: effects.defenseBonus,
    scavengeTurnsDelta: effects.scavengeTurnsDelta,
    // V4 profession-unlock system (plans/i-think-i-want-rosy-stream.md): a
    // Locksmith on the squad skips the search-threat roll entirely (see
    // tactical-turn.mjs). "none" for survivors with no background yet.
    professionCategory: professionCategory(survivor.backgroundId),
  };
}

// Recompute a tactical unit's derived stats from its current equipmentItemIds
// (used after an in-run equip/unequip).
export function recomputeTacticalUnitEquipment(unit) {
  const effects = getEquipmentEffects(unit.equipmentItemIds);
  const baseMove = unit.baseMoveMetersPerTurn ?? DEFAULT_TACTICAL_MOVE_METERS_PER_TURN;
  const baseCarry = unit.baseCarryCapacity ?? DEFAULT_CARRY_CAPACITY;
  return {
    ...unit,
    moveMetersPerTurn: baseMove + effects.moveBonusMetersPerTurn,
    carryCapacity: baseCarry + effects.carryCapacityBonus,
    offenseBonus: effects.offenseBonus,
    defenseBonus: effects.defenseBonus,
    scavengeTurnsDelta: effects.scavengeTurnsDelta,
  };
}

// Equip an equipment item the unit is carrying in its bag. Moves it from the
// bag into its loadout slot; if that slot was occupied, the swapped-out item
// drops back into the bag. Returns a new unit (unchanged if it can't equip).
export function equipCarriedItem(unit, itemId) {
  const inventory = [...(unit.inventory ?? [])];
  const index = inventory.indexOf(itemId);
  if (index === -1 || !getItem(itemId)?.equipment) return unit;
  const slot = equipmentLoadoutSlot(itemId);
  if (!slot) return unit;
  let equipmentItemIds = [...(unit.equipmentItemIds ?? [])];
  inventory.splice(index, 1);
  const occupying = equipmentLoadoutMap(equipmentItemIds).get(slot);
  if (occupying) {
    equipmentItemIds = equipmentItemIds.filter((id) => id !== occupying.itemId);
    inventory.push(occupying.itemId);
  }
  equipmentItemIds.push(itemId);
  return recomputeTacticalUnitEquipment({ ...unit, inventory, equipmentItemIds });
}

// Unequip a loadout item back into the bag — only if the bag has room once the
// item's own capacity bonus is removed. Returns a new unit (unchanged if full).
export function unequipToBag(unit, itemId) {
  const equipmentItemIds = [...(unit.equipmentItemIds ?? [])];
  if (!equipmentItemIds.includes(itemId)) return unit;
  const nextEquipment = equipmentItemIds.filter((id) => id !== itemId);
  const nextCapacity = (unit.baseCarryCapacity ?? DEFAULT_CARRY_CAPACITY) + getEquipmentEffects(nextEquipment).carryCapacityBonus;
  const inventory = [...(unit.inventory ?? [])];
  if (inventory.length >= nextCapacity) return unit;
  inventory.push(itemId);
  return recomputeTacticalUnitEquipment({ ...unit, inventory, equipmentItemIds: nextEquipment });
}

function featureCentroid(feature) {
  const ring = feature.geometry.type === "Polygon" ? feature.geometry.coordinates[0] : feature.geometry.coordinates[0][0];
  return ring.reduce((acc, [lon, lat]) => [acc[0] + lon / ring.length, acc[1] + lat / ring.length], [0, 0]);
}

function nearestPoint(from, points) {
  if (!points.length) return [...from];
  return points.reduce((best, point) => (distanceInMeters(from, point) < distanceInMeters(from, best) ? point : best));
}

// Convert an east/north metre offset at `origin` into a [lon,lat] delta (flat-
// earth approximation, fine over a tactical zone's tens of metres).
function metersToLngLatDelta(origin, eastMeters, northMeters) {
  const metersPerLon = Math.cos(origin[1] * Math.PI / 180) * 111320;
  const metersPerLat = 111320;
  return [eastMeters / metersPerLon, northMeters / metersPerLat];
}

// The squad's entry point: the westernmost boundary crossing (falls back to the
// zone centre if a zone somehow has no crossings). Exported so the app can seed
// the pre-run scavenge preview and the enemy-avoid radius from the same point
// the squad actually spawns at.
export function westernEntryCrossing(zone) {
  const entry = [...zone.boundaryCrossings].sort((a, b) => a.crossingCoordinate[0] - b.crossingCoordinate[0])[0];
  return entry ? [...entry.crossingCoordinate] : [...zone.centerCoordinate];
}

export function routeEntryCoordinateForZone(zone, route) {
  const coordinates = route?.reachable === false ? [] : (route?.coordinates ?? []);
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const start = coordinates[index];
    const end = coordinates[index + 1];
    const startDistance = distanceInMeters(start, zone.centerCoordinate);
    const endDistance = distanceInMeters(end, zone.centerCoordinate);
    if ((startDistance > zone.radiusMeters) === (endDistance > zone.radiusMeters)) continue;
    let lower = 0;
    let upper = 1;
    for (let step = 0; step < 20; step += 1) {
      const mid = (lower + upper) / 2;
      const point = [start[0] + (end[0] - start[0]) * mid, start[1] + (end[1] - start[1]) * mid];
      const distance = distanceInMeters(point, zone.centerCoordinate);
      if ((distance > zone.radiusMeters) === (startDistance > zone.radiusMeters)) lower = mid;
      else upper = mid;
    }
    const mid = (lower + upper) / 2;
    return [start[0] + (end[0] - start[0]) * mid, start[1] + (end[1] - start[1]) * mid];
  }
  return nearestPoint(zone.centerCoordinate, coordinates);
}

function sortedEntryCrossings(zone, preferredEntryCoordinate) {
  const crossings = zone.boundaryCrossings.map((entry) => [...entry.crossingCoordinate]);
  if (!crossings.length) return [];
  if (preferredEntryCoordinate) {
    return crossings.sort((a, b) => distanceInMeters(a, preferredEntryCoordinate) - distanceInMeters(b, preferredEntryCoordinate));
  }
  return crossings.sort((a, b) => a[0] - b[0]);
}

function spawnBasis(zone, entry) {
  const center = zone.centerCoordinate;
  const eastToCenter = (center[0] - entry[0]) * Math.cos(entry[1] * Math.PI / 180) * 111320;
  const northToCenter = (center[1] - entry[1]) * 111320;
  const length = Math.hypot(eastToCenter, northToCenter) || 1;
  const inward = [eastToCenter / length, northToCenter / length];
  const tangent = [-inward[1], inward[0]]; // rotate 90° -> along the boundary
  return { inward, tangent };
}

function offsetFromEntry(entry, basis, inwardMeters, tangentMeters) {
  const east = basis.inward[0] * inwardMeters + basis.tangent[0] * tangentMeters;
  const north = basis.inward[1] * inwardMeters + basis.tangent[1] * tangentMeters;
  const [dLon, dLat] = metersToLngLatDelta(entry, east, north);
  return [entry[0] + dLon, entry[1] + dLat];
}

// A tight triangle just inside one entrance: the lead survivor points inward,
// the other two sit a half-gap to either side (an equilateral formation with
// SQUAD_FORMATION_GAP_METERS sides). Squads larger than three stack into
// further ranks behind, one gap deeper each. Keeps everyone on the same street
// so they discover the neighbourhood together instead of scattering in blind.
function triangleSpawnPositions(zone, entry, count) {
  const basis = spawnBasis(zone, entry);
  const gap = SQUAD_FORMATION_GAP_METERS;
  const halfBase = gap / 2;
  const height = gap * Math.sqrt(3) / 2;
  const pattern = [[height, 0], [0, -halfBase], [0, halfBase]];
  return Array.from({ length: count }, (_, index) => {
    const [extraInward, tangentMeters] = pattern[index % pattern.length];
    const rank = Math.floor(index / pattern.length);
    return offsetFromEntry(entry, basis, SQUAD_SPAWN_INSET_METERS + extraInward + rank * gap, tangentMeters);
  });
}

// Where the squad forms up: a tight triangle at the single entrance nearest the
// visible base→target route's entry point (falling back to the western crossing
// if the route entry is unknown), so the whole squad enters on one street.
export function squadSpawnPositions(zone, count, { preferredEntryCoordinate = null } = {}) {
  if (count <= 0) return [];
  const entries = sortedEntryCrossings(zone, preferredEntryCoordinate);
  const entry = entries[0] ?? westernEntryCrossing(zone);
  return triangleSpawnPositions(zone, entry, count);
}

// The playable geometry for a POI: the tactical zone, its nav grid, and the
// building doorsteps a survivor can enter through. Pure given the injected road
// graph + building set; callers typically cache this per POI.
export function buildTacticalEncounterZone(poi, { roadGraph, buildingCollection, radiusMeters = DEFAULT_TACTICAL_ZONE_RADIUS_METERS }) {
  const zone = buildTacticalZone({ zoneId: poi.id, centerCoordinate: poi.coordinate, radiusMeters, roadGraph, buildingCollection });
  const navGrid = buildNavGrid(zone, { extractionBandMeters: EXTRACTION_BAND_METERS });
  const poiBuilding = zone.buildings.find((feature) => pointInBuildings(poi.coordinate, [feature]))
    || zone.buildings.slice().sort((a, b) => distanceInMeters(featureCentroid(a), poi.coordinate) - distanceInMeters(featureCentroid(b), poi.coordinate))[0]
    || null;
  const poiEntrances = poiBuilding ? buildingDoorsteps(navGrid, poiBuilding) : [];
  return { zone, navGrid, poiBuilding, poiEntrances };
}

// Zone corridors spaced apart so enemies don't all cluster on one block. When
// `avoidCoordinate`/`avoidRadiusMeters` are given, corridors whose seed point
// falls inside that radius (i.e. right on the squad's entry) are deprioritised
// — used only if there aren't otherwise enough corridors to fill `count`.
function pickSpreadCorridors(zone, count, { avoidCoordinate = null, avoidRadiusMeters = 0 } = {}) {
  let candidates = [...zone.corridors.values()].filter((corridor) => distanceInMeters(corridor.coordinates[0], zone.centerCoordinate) < zone.radiusMeters);
  if (avoidCoordinate && avoidRadiusMeters > 0) {
    const far = candidates.filter((corridor) => distanceInMeters(corridor.coordinates[0], avoidCoordinate) >= avoidRadiusMeters);
    const near = candidates.filter((corridor) => distanceInMeters(corridor.coordinates[0], avoidCoordinate) < avoidRadiusMeters);
    candidates = [...far, ...near]; // prefer far-from-spawn; keep near ones only as fallback
  }
  const chosen = [];
  for (const corridor of candidates) {
    if (chosen.length >= count) break;
    if (chosen.every((existing) => distanceInMeters(existing.coordinates[0], corridor.coordinates[0]) > ENEMY_SPAWN_MIN_SPACING_METERS)) chosen.push(corridor);
  }
  while (chosen.length < count && candidates.length) chosen.push(candidates[chosen.length % candidates.length]);
  return chosen;
}

// Spawn `count` wander-state enemies on distinct zone corridors, each already
// walking its corridor as its opening patrol leg. `options` is threaded to
// pickSpreadCorridors (see its avoidCoordinate/avoidRadiusMeters).
export function buildTacticalEnemies(zone, count, options = {}) {
  return pickSpreadCorridors(zone, count, options).map((corridor, index) => ({
    id: `enemy:${index + 1}`,
    behavior: ENEMY_STATES.wander,
    position: [...corridor.coordinates[0]],
    path: corridor.coordinates.map((coordinate) => [...coordinate]),
    pathIndex: 0,
    pathCorridorId: corridor.id,
    targetNodeId: corridor.endNodeId,
    speedMetersPerGameMinute: ENEMY_SPEED_METERS_PER_GAME_MINUTE,
    lastAdvancedAtGameMinute: 0,
    anchorCoordinate: [...corridor.coordinates[0]],
    facingDegrees: index * 90,
  }));
}

export const DEFAULT_GUARD_COUNT = 2;
export const DEFAULT_PATROLLER_COUNT = 2;
const GUARD_MIN_SPACING_METERS = 45;

// The zone's road-graph nodes that sit inside the circle, each with the outward
// directions of every corridor meeting there. A node with 2+ corridors is an
// intersection a guard can hold and look down; degree (corridor count) ranks
// them so guards prefer busy junctions.
function zoneIntersectionNodes(zone) {
  const nodes = new Map(); // nodeId -> { nodeId, coordinate, aways: [coord,...] }
  for (const corridor of zone.corridors.values()) {
    const coordinates = corridor.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) continue;
    const ends = [
      [corridor.startNodeId, coordinates[0], coordinates[1]],
      [corridor.endNodeId, coordinates[coordinates.length - 1], coordinates[coordinates.length - 2]],
    ];
    for (const [nodeId, endpoint, neighbor] of ends) {
      if (!nodeId || !Array.isArray(endpoint) || !Array.isArray(neighbor)) continue;
      if (distanceInMeters(endpoint, zone.centerCoordinate) > zone.radiusMeters) continue;
      let node = nodes.get(nodeId);
      if (!node) { node = { nodeId, coordinate: [...endpoint], aways: [] }; nodes.set(nodeId, node); }
      node.aways.push([...neighbor]);
    }
  }
  return [...nodes.values()].filter((node) => node.aways.length >= 2);
}

// Guards: stationed at intersections (busiest first, spaced apart, kept out of
// the squad's spawn-avoid radius when possible). Each looks down one street of
// its intersection and rotates through the others while calm (tactical-enemy-ai).
export function buildGuards(zone, count, { avoidCoordinate = null, avoidRadiusMeters = 0 } = {}) {
  if (count <= 0) return [];
  const ranked = zoneIntersectionNodes(zone)
    .filter((node) => !avoidCoordinate || distanceInMeters(node.coordinate, avoidCoordinate) >= avoidRadiusMeters)
    .sort((a, b) => b.aways.length - a.aways.length || String(a.nodeId).localeCompare(String(b.nodeId)));
  const chosen = [];
  for (const node of ranked) {
    if (chosen.length >= count) break;
    if (chosen.every((existing) => distanceInMeters(existing.coordinate, node.coordinate) >= GUARD_MIN_SPACING_METERS)) chosen.push(node);
  }
  // Relax spacing if too few intersections cleared it (small/sparse zones).
  for (const node of ranked) {
    if (chosen.length >= count) break;
    if (!chosen.includes(node)) chosen.push(node);
  }
  return chosen.slice(0, count).map((node, index) => {
    const guardFacings = node.aways.map((away) => facingBearingDegrees(node.coordinate, away));
    return {
      id: `guard:${index + 1}`,
      enemyType: ENEMY_TYPES.guard,
      behavior: ENEMY_STATES.wander,
      position: [...node.coordinate],
      anchorCoordinate: [...node.coordinate],
      guardNodeId: node.nodeId,
      guardFacings,
      guardFacingIndex: 0,
      guardRotations: 0,
      // Stagger each guard's FIRST re-aim across the min..max window (by index)
      // so guards in the same supply run don't all pivot on the same turn; from there
      // their hashed 3-5 intervals keep them out of sync.
      guardNextTurnAtGameMinute: GUARD_ROTATE_MIN_TURNS + (index % (GUARD_ROTATE_MAX_TURNS - GUARD_ROTATE_MIN_TURNS + 1)),
      facingDegrees: guardFacings[0] ?? 0,
      speedMetersPerGameMinute: ENEMY_SPEED_METERS_PER_GAME_MINUTE,
      lastAdvancedAtGameMinute: 0,
      path: [[...node.coordinate]],
      pathIndex: 0,
    };
  });
}

// Patrollers: each walks one street (corridor) back and forth between its two
// intersections, pausing a full turn to about-face at each end (tactical-enemy-ai).
export function buildPatrollers(zone, count, options = {}) {
  if (count <= 0) return [];
  return pickSpreadCorridors(zone, count, options).map((corridor, index) => {
    const coordinates = corridor.coordinates.map((coordinate) => [...coordinate]);
    return {
      id: `patroller:${index + 1}`,
      enemyType: ENEMY_TYPES.patroller,
      behavior: ENEMY_STATES.wander,
      position: [...coordinates[0]],
      path: coordinates,
      patrolCoordinates: coordinates.map((coordinate) => [...coordinate]),
      patrolCorridorId: corridor.id,
      patrolTurning: false,
      pathIndex: 0,
      pathCorridorId: corridor.id,
      targetNodeId: corridor.endNodeId,
      speedMetersPerGameMinute: ENEMY_SPEED_METERS_PER_GAME_MINUTE,
      lastAdvancedAtGameMinute: 0,
      anchorCoordinate: [...coordinates[0]],
      facingDegrees: facingBearingDegrees(coordinates[0], coordinates[1] ?? coordinates[0]),
    };
  });
}

// The starting enemy force for a supply run: guards at intersections + patrollers on
// streets. `options` (avoidCoordinate/avoidRadiusMeters) keeps both off the
// squad's entry. Threat reinforcements (spawnThreatEnemy) still layer on later.
export function buildTacticalForce(zone, { guards = DEFAULT_GUARD_COUNT, patrollers = DEFAULT_PATROLLER_COUNT } = {}, options = {}) {
  return [...buildGuards(zone, guards, options), ...buildPatrollers(zone, patrollers, options)];
}

// Resolve a single POI supply run headlessly to completion and report a per-survivor
// outcome (died / banked items / value). `enemyCount` is supplied by the caller
// (typically threatToEnemyCount of the POI's current threat). `squadUnits` are
// createTacticalUnit() results; they're positioned at supply-run entry crossings.
export function resolveSupplyRun({
  poi,
  zoneInfo,
  squadUnits,
  enemyCount,
  roadGraph,
  offStreetMultiplier = DEFAULT_OFF_STREET_MULTIPLIER,
  visionConeDegrees = DEFAULT_TACTICAL_VISION_CONE_DEGREES,
  sightMeters = DEFAULT_TACTICAL_SIGHT_METERS,
  maxTurns,
}) {
  const { zone, navGrid, poiEntrances } = zoneInfo;
  const entry = westernEntryCrossing(zone);
  const positions = squadSpawnPositions(zone, squadUnits.length);
  const units = squadUnits.map((unit, index) => ({ ...unit, position: positions[index] }));

  const enemies = buildTacticalEnemies(zone, enemyCount, { avoidCoordinate: entry, avoidRadiusMeters: ENEMY_SPAWN_AVOID_SPAWN_METERS });
  const tacticalState0 = createTacticalState({ zoneId: zone.zoneId, units, enemies });

  const context = {
    zone,
    graph: { corridors: zone.corridors },
    routeTo: (from, to) => {
      const route = findRouteBetweenPoints(roadGraph, from, to);
      return route?.reachable ? route : { reachable: true, coordinates: [[...from], [...to]], distanceMeters: distanceInMeters(from, to) };
    },
    visionConeDegrees,
    sightMeters,
    occluders: zone.buildings,
    terrain: { corridors: zone.corridors, offStreetMultiplier },
  };

  const policy = {
    poiCoordinate: poi.coordinate,
    poiEntranceFor: (unit) => (poiEntrances.length ? nearestPoint(unit.position, poiEntrances) : [...poi.coordinate]),
    nearestCrossingFor: (unit) => nearestPoint(unit.position, zone.boundaryCrossings.map((crossing) => crossing.crossingCoordinate)),
    routePath: (from, to) => {
      const result = findTerrainPath(navGrid, from, to, { offStreetMultiplier });
      return result.coordinates.length >= 2 ? result.coordinates : [[...from]];
    },
  };

  let finalState = runTacticalEncounterToCompletion(tacticalState0, context, policy, maxTurns ? { maxTurns } : undefined);

  // Anyone still active after the turn cap is treated as "made it out late" —
  // a synthetic extraction so the supply run's accounting always closes. This is a
  // rare pre-existing tactical-pathfinding edge case (a computed path can land
  // just outside EXTRACTION_RANGE_METERS; see plans/V4_APP_REWRITE_PLAN.md),
  // not a failure mode this module introduces. The real app's playable supply run
  // mode will replace this with a "close enough, extract" affordance.
  const strandedIds = new Set(Object.keys(finalState.units));
  for (const unit of Object.values(finalState.units)) {
    finalState = {
      ...finalState,
      extractedUnitIds: [...finalState.extractedUnitIds, unit.id],
      log: [...finalState.log, { type: "unit-extracted", unitId: unit.id, survivorId: unit.survivorId, inventory: [...(unit.inventory || [])], coordinate: [...unit.position] }],
    };
  }

  const survivorOutcomes = supplyRunOutcomeFromState(finalState, squadUnits, { strandedIds });
  return { finalState, survivorOutcomes, enemyCount, turns: finalState.turnNumber };
}

// Per-survivor outcome (died / banked items / value) from a finished supply run's
// tactical state. Shared by resolveSupplyRun (headless) and the app's interactive
// supply-run mode (tactical-view), which both need the same death/supply accounting — it
// reuses permadeath.mjs's resolveTacticalZoneOutcome, so a dead unit never
// banks the supplies it was carrying. `squadUnits` carry { id, survivorId }.
export function supplyRunOutcomeFromState(finalState, squadUnits, { strandedIds } = {}) {
  // permadeath.mjs only touches campaign.survivorsById, so a minimal duck-typed
  // campaign is enough to reuse its real death/supply accounting.
  const minimalCampaign = {
    survivorsById: Object.fromEntries(squadUnits.map((unit) => [unit.survivorId, { id: unit.survivorId, status: "available", health: { current: 10, max: 10 } }])),
  };
  const { extractions, deadUnitIds } = resolveTacticalZoneOutcome(minimalCampaign, finalState);

  return squadUnits.map((unit) => {
    const died = deadUnitIds.includes(unit.id);
    const extraction = extractions.find((entry) => entry.unitId === unit.id);
    // Final combat HP for a survivor who made it out — from the extraction event,
    // or from the live unit if they ended the run stranded on the map. Dead
    // survivors carry none. Lets the campaign persist wounds taken in the supply run.
    const finalHealth = !died ? (extraction?.health ?? finalState.units?.[unit.id]?.health) : undefined;
    return {
      survivorId: unit.survivorId,
      died,
      items: extraction?.items ?? [],
      value: extraction ? inventoryValue(extraction.items) : 0,
      // The loadout the survivor extracted with (may differ if they equipped
      // gear mid-run), so the campaign can persist it. Absent for the dead.
      ...(extraction?.equipmentItemIds ? { equipmentItemIds: extraction.equipmentItemIds } : {}),
      ...(finalHealth ? { health: { current: finalHealth.current, max: finalHealth.max } } : {}),
      stranded: Boolean(!died && strandedIds?.has(unit.id)),
    };
  });
}

// The base-defense counterpart to raidOutcomeFromState: read the finished
// defense's tactical state into the payload campaign-v4's applyDefenseOutcome
// consumes. Every defender is a unit whose id IS its survivorId (createRaidUnit),
// so a dead unit's id is a survivor id directly. `result` (repelled /
// safe-room-taken / overrun) and `waveSize` are decided by the caller's win/lose
// check (which owns the base geometry); this just tallies HP, deaths, and doors.
export function defenseOutcomeFromState(finalState, { result, waveSize }) {
  const rosterHealth = Object.values(finalState.units || {}).map((unit) => ({
    id: unit.survivorId ?? unit.id,
    health: unit.health ? { current: unit.health.current, max: unit.health.max } : undefined,
  }));
  const deaths = [...(finalState.deadUnitIds || [])];
  const breachedDoorCount = Object.values(finalState.fieldObjects || {}).filter((object) => object.breached).length;
  return { result, waveSize, rosterHealth, breachedDoorCount, deaths };
}
